import { PricingWithTaxesInput } from '@/classes';
import {
  DEFAULT_PROCESSING_FEE,
  TAX_EXEMPT_PRICING_NO_LABEL,
} from '@/constants';
import { useTaxRates } from '@/hooks';
import {
  calculatePlatformFeeInDollars,
  formatPricing,
  formatTo2DecimalPlaces,
  mapTaxRateOptions,
  parseFloatIfString,
} from '@/utils';
import {
  Grid,
  InfoIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import {
  Control,
  Controller,
  FieldValues,
  FormState,
  UseFormWatch,
} from 'react-hook-form';
import {
  LeftStyledInputField,
  RightStyledInputField,
} from './PricingWithTaxes.styles';
import { InputAdornment } from '@mui/material';
import { useEffect, useState } from 'react';
import { StyledTable } from './PricingWithTaxes.styles';

interface PricingWithTaxesProps<T extends FieldValues> {
  control: Control<T>;
  formState: FormState<T>;
  watch: UseFormWatch<T>;

  disabled?: boolean;
  platformFeeRaw?: number;
  title?: string;
  displayBreakdown?: boolean;
  isFlatFee?: boolean;
}

interface PricingBreakdownProps {
  platformFee: number;
  processingFeeInDollars: number;
  totalPrice: number;
  salesTaxAmount?: number;
}

const PricingBreakdown = ({
  platformFee,
  processingFeeInDollars,
  totalPrice,
  salesTaxAmount = 0,
}: PricingBreakdownProps) => {
  if (totalPrice === 0) {
    return <>Free</>;
  }
  const totalPlatformFee = platformFee + processingFeeInDollars;
  return (
    <StyledTable>
      <tbody>
        <tr>
          <td>Guest Price</td>
          <td>{formatPricing(totalPrice, 2)}</td>
        </tr>
        <tr>
          <td>Tax</td>
          <td>{formatPricing(-salesTaxAmount, 2)}</td>
        </tr>
        <tr>
          <td>Platform Fee</td>
          <td>{formatPricing(-totalPlatformFee, 2)}</td>
        </tr>
        <tr>
          <td>Net Revenue</td>
          <td>
            {formatPricing(totalPrice - salesTaxAmount - totalPlatformFee, 2)}
          </td>
        </tr>
      </tbody>
    </StyledTable>
  );
};

export const PricingWithTaxes = ({
  platformFeeRaw,
  title,
  control,
  formState,
  watch,
  disabled,
  displayBreakdown = false,
  isFlatFee = false,
}: PricingWithTaxesProps<PricingWithTaxesInput>) => {
  const { errors } = formState;

  const { data: taxRates } = useTaxRates();
  const theme = useTheme();

  const price = parseFloatIfString(watch('amount'));
  const isInputEmpty = watch('amount') === '';
  const taxRate = watch('taxRateId') || '0';
  const taxPercentage = taxRates?.find(
    oneTaxRate => oneTaxRate.id === taxRate
  )?.percentage;
  const salesTaxAmount =
    !!taxRates && !!taxPercentage
      ? formatTo2DecimalPlaces((price * taxPercentage) / 100)
      : 0;
  const totalPrice = salesTaxAmount + price;

  const processingFeeInDollars = taxPercentage
    ? formatTo2DecimalPlaces(
        (price * taxPercentage * (DEFAULT_PROCESSING_FEE / 10000)) / 100
      )
    : 0;

  const [platformFee, setPlatformFee] = useState(
    platformFeeRaw ? calculatePlatformFeeInDollars(platformFeeRaw, price) : 0
  );

  // Re-calculate fees when totalPrice/eventType changes
  useEffect(() => {
    if (platformFeeRaw) {
      const platformFeeInDollars = calculatePlatformFeeInDollars(
        platformFeeRaw,
        price
      );
      setPlatformFee(platformFeeInDollars);
    }
  }, [price, platformFeeRaw]);

  return (
    <Grid
      container
      sx={{
        // Disable the pricing with taxes component
        opacity: disabled ? 0.3 : 1,
        pointerEvents: disabled ? 'none' : 'initial',
      }}
    >
      {!!title ? (
        <Grid item xs={12}>
          <Typography marginBottom={theme.spacers.s} variant="subtitle2">
            {title}
          </Typography>
        </Grid>
      ) : null}
      <Grid item xs={6}>
        <Controller
          control={control}
          name="amount"
          render={({ field }) => (
            <LeftStyledInputField
              {...field}
              inputProps={{ 'data-testid': field.name }}
              label={isFlatFee ? 'Price' : 'Price/person'}
              isPriceInput
              type="number"
              helperText={errors.amount?.message}
              error={!!errors.amount}
              endAdornment={
                !isInputEmpty && displayBreakdown ? (
                  <InputAdornment position="start">
                    <Tooltip
                      title={
                        <PricingBreakdown
                          platformFee={platformFee}
                          processingFeeInDollars={processingFeeInDollars}
                          totalPrice={totalPrice}
                          salesTaxAmount={salesTaxAmount}
                        />
                      }
                      placement="left"
                      arrow
                    >
                      <InfoIcon
                        width={24}
                        color={theme.palette.primary.medium}
                      />
                    </Tooltip>
                  </InputAdornment>
                ) : null
              }
            />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="taxRateId"
          render={({ field }) => (
            <RightStyledInputField
              {...field}
              inputProps={{ 'data-testid': field.name }}
              select
              disabled={totalPrice === 0}
              dropdownItems={
                taxRates
                  ? [
                      TAX_EXEMPT_PRICING_NO_LABEL,
                      ...mapTaxRateOptions(taxRates, false),
                    ]
                  : [{ id: 0, label: 'Loading...', value: '0' }]
              }
              label="Tax rate"
              error={!!errors.taxRateId}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
